<template>
	<div id="upload-file-content">
		<div class="vx-row">
			<div class="vx-col md:w-1/4 w-full">
				<vs-tooltip
					color="success"
					:text="$t('UploadFiles.UploadTypeSelectToltip')"
					position="top"
					:active="true"
				>
					<label class="vs-input--label"
						>{{ $t("UploadFiles.UploadType") }}
						<i class="material-icons ml-1 text-center" style="font-size: 14px"> info </i>
					</label>
					<v-select
						class="select-large"
						v-model="uploadType"
						:clearable="false"
						:options="uploadTypeOptions"
						:dir="$vs.rtl ? 'rtl' : 'ltr'"
						:placeholder="$t('Select')"
					>
						<template v-slot:no-options="{search, searching}">
							<template v-if="searching">
								{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
							</template>
							<template v-else>
								{{ $t("NoResultsFound") }}
							</template>
						</template>
					</v-select>
				</vs-tooltip>
			</div>
		</div>
		<div class="vx-row">
			<div :class="dataFile ? 'vx-col md:w-3/4 w-full' : 'vx-col md:w-4/4 w-full'">
				<vs-input
					id="file-upload"
					ref="fileUpload"
					type="file"
					class="w-full"
					:disabled="!uploadType"
					accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.json,.csv,.xml"
					@change="onFileChange"
				/>
				<vs-alert :active="!validateFileTypes" color="danger" icon="new_releases" class="mt-2">
					{{ $t("UploadFiles.InvalidFormatFileText") }}
				</vs-alert>
				<div
					class="custom-file-upload mt-2 relative"
					:class="{'file-upload-height--medium': true}"
					@dragover="dragover"
					@dragleave="dragleave"
					@drop="drop"
				>
					<!-- Enhanced Progress Overlay con barra de progreso moderna -->
					<div v-if="showProgressOverlay" class="enhanced-upload-progress-overlay">
						<div class="modern-progress-container">
							<!-- Header del progreso -->
							<div class="progress-header">
								<div
									class="progress-icon-modern"
									:class="{
										'success-icon': showSuccessState,
										'redirecting-icon': showRedirectingState,
									}"
								>
									<feather-icon
										:icon="
											showRedirectingState
												? 'ArrowRightIcon'
												: showSuccessState
												? 'CheckCircleIcon'
												: 'UploadCloudIcon'
										"
										class="upload-cloud-icon"
										:class="{
											'animate-bounce':
												progressPercent < 100 && !showSuccessState && !showRedirectingState,
											'success-pulse': showSuccessState && !showRedirectingState,
											'redirecting-pulse': showRedirectingState,
										}"
									/>
								</div>
								<div class="progress-status">
									<h4
										class="progress-main-title"
										:class="{'redirecting-title': showRedirectingState}"
									>
										{{
											showRedirectingState
												? $t("UploadFiles.RedirectingToMapping")
												: showSuccessState
												? $t("UploadFiles.UploadSuccessful")
												: $t("UploadFiles.UploadingFile")
										}}
									</h4>
									<p class="progress-file-name" v-if="!showRedirectingState">
										{{ fileName || $t("UploadFiles.ProcessingFile") }}
									</p>

									<!-- Indicador visual de redirección -->
									<div v-if="showRedirectingState" class="redirecting-indicator">
										<div class="redirecting-dots">
											<span class="dot"></span>
											<span class="dot"></span>
											<span class="dot"></span>
										</div>
										<p class="redirecting-subtitle">
											{{ $t("UploadFiles.PleaseWait") }}
										</p>
									</div>
								</div>
							</div>

							<!-- Barra de progreso moderna -->
							<div class="modern-progress-section">
								<div class="progress-stats">
									<span class="progress-label">{{ $t("UploadFiles.Progress") }}</span>
									<span class="progress-percent">{{ progressPercent }}%</span>
								</div>

								<!-- Barra de progreso personalizada -->
								<div class="custom-progress-bar">
									<div class="progress-track">
										<div
											class="progress-fill"
											:style="{width: progressPercent + '%'}"
											:class="{'progress-complete': progressPercent === 100}"
										>
											<div class="progress-shine"></div>
										</div>
									</div>
								</div>

								<!-- Indicador de velocidad/estado -->
								<div class="progress-details">
									<div class="upload-speed" v-if="progressPercent < 100">
										<feather-icon icon="ZapIcon" class="w-3 h-3 mr-1" />
										<span>{{ $t("UploadFiles.Uploading") }}...</span>
									</div>
									<div class="upload-complete" v-else-if="!showSuccessState">
										<feather-icon icon="CheckCircleIcon" class="w-3 h-3 mr-1 text-success" />
										<span class="text-success">{{ $t("UploadFiles.Complete") }}</span>
									</div>
									<div class="upload-success-message" v-else>
										<feather-icon icon="CheckIcon" class="w-3 h-3 mr-1 text-success" />
										<span class="text-success font-semibold">{{
											$t("UploadFiles.UploadSuccessMessage")
										}}</span>
									</div>
								</div>
							</div>

							<!-- Acciones -->
							<div class="progress-actions-modern" v-if="!showSuccessState">
								<vs-button
									type="border"
									color="danger"
									size="small"
									icon-pack="feather"
									icon="icon-x"
									class="cancel-upload-btn"
									@click.prevent="cancelUpload"
								>
									{{ $t("Cancel") }}
								</vs-button>
							</div>
						</div>
					</div>

					<label
						class="custom-file-upload__icon"
						:class="uploadStatus"
						:style="!uploadType ? 'color: #8080806e' : ''"
					>
						<feather-icon
							v-if="!dataFile && !isDragging"
							class="mr-2"
							icon="UploadIcon"
							svgClasses="h-10 w-10"
							:style="uploadType ? 'animation: bounce 1s infinite' : ''"
						/>
						<feather-icon v-else class="mr-2" icon="FileTextIcon" svgClasses="h-10 w-10" />
					</label>
					<label
						class="font-bold text-center mb-2"
						:class="uploadStatus"
						:style="!uploadType ? 'color: #8080806e' : ''"
					>
						{{
							this.dataFile
								? this.fileName
								: isDragging
								? $t("DropFile")
								: $t("UploadFiles.UploadFileDragAndDrop")
						}}<br />
						<span v-if="!this.dataFile && !isDragging">{{ $t("Or") }}</span
						><br />
					</label>
					<label
						v-if="!this.dataFile && !isDragging"
						class="custom-file-upload__text font-bold text-center"
						:class="uploadStatus"
					>
						<label
							for="file-upload"
							class="p-2 text-xs md:text-sm font-semibold border rounded cursor-pointer"
							:class="uploadType ? 'bg-primary--transparent hover:bg-primary hover:shadow-sm' : 'bg-grey'"
						>
							<span class="text-white">{{ $t("UploadFiles.SelectFile") }}</span> </label
						><br /><br />
						<small class="text-xs" :style="!uploadType ? 'color: #8080806e' : 'color: #8d8181'">{{
							$t("AllowedFileExtensions") + ": *.xlsx, *.xls, *.csv, *.txt, *.json, *.xml"
						}}</small>
					</label>

					<!-- File Ready Section -->
					<div v-if="dataFile && !showProgressOverlay" class="file-ready-section">
						<div class="file-status-info">
							<feather-icon icon="CheckCircleIcon" class="w-5 h-5 text-success mr-2" />
							<span class="text-success font-medium">{{ $t("UploadFiles.FileReadyToBeUploaded") }}</span>
						</div>
						<div class="file-actions mt-3">
							<vs-button
								color="danger"
								icon-pack="feather"
								icon="icon-x"
								type="border"
								size="small"
								:title="$t('RemoveSelection')"
								@click.prevent="cleanData"
							>
								{{ $t("RemoveSelection") }}
							</vs-button>
						</div>
					</div>
				</div>
				<p class="mt-4 mb-base">
					<vs-alert
						:active.sync="activeAlert"
						closable
						icon-pack="feather"
						close-icon="icon-x"
						color="success"
						:title="$t('UploadFiles.UploadSuccess')"
					>
						{{ $t("UploadFiles.UploadSuccessTextAlert") }}
					</vs-alert>
				</p>
				<vs-alert
					v-if="status !== null && dataFile"
					class="mt-4 mb-base"
					icon-pack="feather"
					close-icon="icon-x"
					:color="status ? 'success' : 'danger'"
					:title="status ? $t('UploadFiles.UploadSuccess') : $t('UploadFiles.UploadError')"
				>
					{{ status ? $t("UploadFiles.UploadSuccessTextAlert") : $t("UploadFiles.UploadErrorMessage") }}.
				</vs-alert>
			</div>
			<div class="vx-col w-full md:w-1/4 lg:w-1/4 xl:w-1/4">
				<vx-card no-shadow v-if="dataFile">
					<template slot="no-body">
						<div class="p-8 clearfix">
							<div>
								<h1>
									<sup class="text-lg"></sup>
									<span>{{ $t("UploadFiles.Details") }}</span>
								</h1>
								<small>
									<span class="text-grey">{{ $t("Name") }}: </span>
									<span>{{ dataFile.name }}</span>
								</small>
							</div>
							<p class="mt-2 mb-8 text-xl font-medium" :class="10 >= 0 ? 'text-success' : 'text-danger'">
								<span>{{ $t("UploadFiles.Size") }}:</span>
								<span class="ml-1">{{ fileSize }}</span>
							</p>
						</div>
						<div class="p-8 border d-theme-border-grey-light border-solid border-r-0 border-l-0 border-b-0">
							<div class="mb-1">
								<small>{{ $t("UploadFiles.FileReadyToBeUploaded") }}</small>
							</div>
						</div>
					</template>
				</vx-card>
			</div>
		</div>
		<div class="vx-row">
			<div class="vx-col w-full">
				<div class="mb-4 flex flex-wrap items-center justify-end">
					<vs-button color="danger" icon-pack="feather" icon="icon-x" @click.prevent="cleanData"
						>{{ $t("Cancel") }}
					</vs-button>
					<vs-button
						icon-pack="feather"
						icon="icon-upload"
						class="ml-2"
						:disabled="enableUploadBtn || !validateFileTypes"
						@click.prevent="handleUpload"
						>{{ $t("Upload") }}
					</vs-button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import vSelect from "vue-select";
import firebase from "firebase/app";
import "firebase/storage";
import {uploadFileToFirebase} from "@/helpers/firebaseDB.js";
import UploadFile from "@/models/UploadFile.js";
import ManageErrorMessages from "@/models/ManageErrorMessages.js";
import moduleCommon from "@/store/common/moduleCommon.js";
import FloatingButtons from "@/views/FloatingButtons.vue";

export default {
	components: {
		vSelect,
		FloatingButtons,
	},
	data() {
		return {
			storageRef: firebase.storage(),
			firebasePath: null,
			percent: 0,
			dataFile: null,
			fileName: "",
			textStatus: "",
			progressColor: "success",
			isDragging: false,
			uploadType: null,
			activeAlert: false,
			status: null,
			manageErrorMessages: new ManageErrorMessages({that: this}),
			selectedCountry: null,
			is_indeterminate: false,
			uploadTypeOptions: [
				{label: this.$t("RouterSelection.VisitPoints"), value: "VISIT_POINT", category: "VISIT_POINT_MASSIVE"},
				{label: this.$t("Vehicles.Vehicles"), value: "VEHICLE", category: "VEHICLE_MASSIVE"},
			],
			firesbaseResponse: {
				downloadURL: "",
				bucketPath: "",
			},
			// Enhanced UX properties
			showProgressOverlay: false,
			uploadCancelled: false,
			progressInterval: null,
			showSuccessState: false,
			showRedirectingState: false,
			uploadStartTime: null,
		};
	},
	computed: {
		enableUploadBtn() {
			return !this.dataFile || this.uploadType === null;
		},
		validateFileTypes() {
			if (this.dataFile) {
				const fileExt = this.dataFile.name.split(".").pop();
				let filterFormat = this.fieldTypes.filter((format) => format.label === fileExt);
				return filterFormat.length > 0;
			}
			return true;
		},
		uploadStatus() {
			if (this.textStatus === "success" && this.validateFileTypes) return "text-success";
			else if (this.textStatus === "error" || !this.validateFileTypes) return "text-danger";
			else return "";
		},
		fileSize() {
			const fileBytes = this.dataFile.size;
			if (fileBytes >= 1073741824) {
				return (fileBytes / 1073741824).toFixed(2) + " GB";
			} else if (fileBytes >= 1048576) {
				return (fileBytes / 1048576).toFixed(2) + " MB";
			} else if (fileBytes >= 1024) {
				return (fileBytes / 1024).toFixed(2) + " KB";
			} else if (fileBytes > 1) {
				return fileBytes + " bytes";
			} else if (fileBytes == 1) {
				return fileBytes + " byte";
			} else {
				return "0 bytes";
			}
		},
		progressPercent: {
			get() {
				return this.$store.state.uploadFile.uploadProgress;
			},
			set(value) {},
		},
		fieldTypes() {
			return [
				{
					label: `xlsx`,
					icon: "arrow_right",
				},
				{
					label: `csv`,
					icon: "arrow_right",
				},
				{
					label: `json`,
					icon: "arrow_right",
				},
				{
					label: `xml`,
					icon: "arrow_right",
				},
				{
					label: `txt`,
					icon: "arrow_right",
				},
			];
		},
		countriesOptions() {
			return this.generateOptionSelect(
				this.$store.state.moduleCommon.countries_sitidata,
				"country_name",
				"country_name"
			);
		},
	},
	methods: {
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		handleUpload() {
			if (this.uploadCancelled) return;

			const uploadType = this.uploadType.value;
			const uploadTypeObject = {
				VISIT_POINT: "/uploads/visit_point/",
				VEHICLE: "/uploads/vehicle/",
			};
			this.firebasePath = uploadTypeObject[uploadType] || null;

			// Show progress overlay and record start time
			this.showProgressOverlay = true;
			this.progressPercent = 0;
			this.is_indeterminate = false;
			this.uploadStartTime = Date.now();

			// Start progress simulation
			this.startProgressSimulation();

			this.uploadOtherFile();
		},
		successUpload() {
			// Reset all states and hide progress overlay
			this.showSuccessState = false;
			this.showRedirectingState = false;
			this.showProgressOverlay = false;

			this.$vs.notify({
				color: "success",
				title: this.$t("UploadFiles.UploadSuccess"),
				text: this.$t("UploadFiles.UploadSuccessMessage"),
			});

			this.cleanData();
		},
		onFileChange(e) {
			const files = e.target.files || e.dataTransfer.files;
			if (!files.length) return;
			this.dataFile = files[0];
			this.fileName = this.dataFile.name;
			this.percent = 0;
			this.textStatus = "success";
			this.progressColor = "success";
		},
		/**
		 * Subir arcchivos de tipologias VISIT_POINT, VEHICLE
		 */
		uploadOtherFile() {
			const fileExt = this.dataFile.name.split(".").pop();
			if (!this.dataFile) {
				return;
			}
			let filterFormat = this.fieldTypes.filter((format) => format.label === fileExt);
			if (filterFormat.length <= 0) {
				this.$vs.notify({
					time: 9000,
					title: this.$t("UploadFiles.InvalidFormatFile"),
					text: this.$t("UploadFiles.InvalidFormatFileText"),
					iconPack: "feather",
					icon: "icon-alert-circle",
					color: "danger",
				});
			} else {
				let uploadFile = null;
				this.is_indeterminate = true;
				uploadFileToFirebase
					.call(this, this.dataFile, this.firebasePath)
					.then((res) => {
						Object.assign(this.firesbaseResponse, res);
						const payload = {
							bucket_path: this.firesbaseResponse.bucketPath,
							original_name: this.fileName,
							public: true,
							typology_id: this.uploadType.value,
							category: this.uploadType.category,
						};
						uploadFile = new UploadFile(payload);
						return this.$store.dispatch("uploadFile/saveUploadFile", uploadFile).then((res) => {
							if (Object.entries(res).length > 0) {
								this.percent = 100;
								this.$store.commit("uploadFile/incrementProgress", 100);
								this.is_indeterminate = true;
							}
							return res;
						});
					})
					.then((res) => {
						const uploaded = res;
						this.$store.commit("uploadFile/SAVE_UPLOAD", uploaded);
						uploadFile.id = res.id;
						this.completeProgress();
					})
					.catch((error) => {
						this.textStatus = "error";
						this.progressColor = "danger";
						if (Object.hasOwnProperty.call(error, "code")) {
							this.manageErrorMessages.setErrorMessage(error.code);
						}
						if (Object.hasOwnProperty.call(error, "data")) {
							this.status = false;
						}
						this.manageErrorMessages.showMessage();
					});
			}
		},
		cleanData() {
			// Clear progress interval
			if (this.progressInterval) {
				clearInterval(this.progressInterval);
				this.progressInterval = null;
			}

			this.fileName = this.$t("UploadFiles.SelectFile");
			this.dataFile = null;
			this.textStatus = "";
			this.progressColor = "primary";
			this.status = null;
			this.showProgressOverlay = false;
			this.showSuccessState = false;
			this.showRedirectingState = false;
			this.uploadStartTime = null;
			this.uploadCancelled = false;
			this.$store.commit("uploadFile/resetProgress");
		},
		showSuccessUpload() {
			this.activeAlert = true;
		},
		generateOptionSelect(data, propLabel, propValue) {
			return data.map((d) => {
				return {label: d[propLabel], value: d[propValue]};
			});
		},
		dragover(event) {
			if (!this.uploadType) return;
			event.preventDefault();
			// Add some visual fluff to show the user can drop its files
			if (!event.currentTarget.classList.contains("bg-success--transparent")) {
				event.currentTarget.classList.remove("bg-gray");
				event.currentTarget.classList.add("bg-success--transparent");
			}
			this.isDragging = true;
		},
		dragleave(event) {
			if (!this.uploadType) return;
			// Clean up
			event.currentTarget.classList.add("bg-gray");
			event.currentTarget.classList.remove("bg-success--transparent");
			this.isDragging = false;
		},
		drop(event) {
			if (!this.uploadType) return;
			event.preventDefault();
			this.onFileChange(event); // Trigger the onChange event manually
			// Clean up
			event.currentTarget.classList.add("bg-gray");
			event.currentTarget.classList.remove("bg-success--transparent");
			this.isDragging = false;
		},
		startProgressSimulation() {
			if (this.progressInterval) {
				clearInterval(this.progressInterval);
			}

			const secureRandom = () => {
				const array = new Uint32Array(1);
				window.crypto.getRandomValues(array);
				return array[0] / (0xffffffff + 1);
			};

			const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3); // Curva suave

			let currentProgress = 0;
			const targetProgress = 98; // No pasar de 98%
			const duration = 8000; // Duración total estimada en ms
			const startTime = performance.now();

			this.progressInterval = setInterval(() => {
				if (this.uploadCancelled) {
					clearInterval(this.progressInterval);
					return;
				}

				const elapsed = performance.now() - startTime;
				let t = elapsed / duration;

				// Limitar t entre 0 y 1
				if (t > 1) t = 1;

				// Aplicar curva easeOutCubic
				currentProgress = targetProgress * easeOutCubic(t);

				// Añadir pequeñas variaciones aleatorias seguras
				currentProgress += secureRandom() * 0.3;

				// Evitar pasar de targetProgress
				if (currentProgress > targetProgress) {
					currentProgress = targetProgress;
				}

				this.progressPercent = Math.floor(currentProgress);
			}, 80); // Intervalo fijo para suavidad
		},
		completeProgress() {
			// Clear simulation interval
			if (this.progressInterval) {
				clearInterval(this.progressInterval);
			}

			// Ensure minimum time for user to see progress
			const currentTime = Date.now();
			const uploadStartTime = this.uploadStartTime || currentTime;
			const elapsedTime = currentTime - uploadStartTime;
			const minimumDisplayTime = 3000; // 3 seconds minimum

			if (elapsedTime < minimumDisplayTime) {
				// If upload was too fast, continue the animation to minimum time
				const remainingTime = minimumDisplayTime - elapsedTime;
				const targetProgress = this.progressPercent || 0;

				// Animate from current progress to 100% over remaining time
				this.animateToCompletion(targetProgress, remainingTime);
			} else {
				// Upload took enough time, complete immediately
				this.finishProgressSequence();
			}
		},

		animateToCompletion(startProgress, duration) {
			const increment = (100 - startProgress) / (duration / 50); // Update every 50ms
			let currentProgress = startProgress;

			const animationInterval = setInterval(() => {
				currentProgress += increment;
				if (currentProgress >= 100) {
					currentProgress = 100;
					this.progressPercent = 100;
					clearInterval(animationInterval);
					this.finishProgressSequence();
				} else {
					this.progressPercent = Math.floor(currentProgress);
				}
			}, 50);
		},

		finishProgressSequence() {
			// Complete the progress
			this.progressPercent = 100;

			// Show success state after reaching 100%
			setTimeout(() => {
				this.showSuccessState = true;
			}, 300);

			// Show redirecting message after success state
			setTimeout(() => {
				this.showRedirectingState = true;
			}, 2000); // 2 seconds after success

			// Hide overlay and continue after showing redirecting message
			setTimeout(() => {
				this.successUpload();
				this.$emit("uploadStatusEmit", true);
			}, 3500); // 3.5 seconds total to show both messages
		},

		cancelUpload() {
			this.uploadCancelled = true;
			this.cleanData();
		},
	},
	watch: {
		uploadType(val) {
			this.cleanData();
		},
	},
	created() {
		if (!moduleCommon.isRegistered) {
			this.$store.registerModule("moduleCommon", moduleCommon);
			moduleCommon.isRegistered = true;
		}
		this.$store.commit("uploadFile/resetProgress");
	},
	beforeDestroy() {
		// Clear any running intervals
		if (this.progressInterval) {
			clearInterval(this.progressInterval);
		}
	},
};
</script>

<style lang="scss">
#upload-file-content {
	height: 100%;
}

.select-large {
	.vs__dropdown-toggle {
		border: 2px solid rgba(var(--vs-primary), 0.4);
	}
	.vs__dropdown-menu {
		border: 1px solid rgba(var(--vs-primary), 0.5);
	}
}

input[type="file"] {
	display: none;
}

.custom-file-upload {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	border: 2px dashed #ccc;
	border-radius: 5px;
	height: 300px;
}

.custom-file-upload__icon {
	padding: 50px 100% 0px 100%;
}

.custom-file-upload__text {
	padding: 0px 50px 50px 50px;
}

.custom-heigh {
	height: 70vh;
}

.btn--span-text {
	display: block;
}

.bg-success--transparent {
	background-color: rgba(var(--vs-success), 0.2) !important;
}

.bg-primary--transparent {
	background-color: rgba(var(--vs-primary), 0.8) !important;
}
@media only screen and (min-width: 481px) {
	.btn--span-text {
		display: block;
	}
}

@media only screen and (min-width: 768px) {
	.btn--span-text {
		display: none;
	}
}

@media only screen and (min-width: 1289px) {
	.btn--span-text {
		display: block;
	}
}

/* Enhanced Modern Progress Overlay */
.file-upload-height--medium {
	height: 320px !important;
}

.enhanced-upload-progress-overlay {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.95) 100%);
	backdrop-filter: blur(12px);
	border-radius: 8px;
	border: 2px solid rgba(var(--vs-primary), 0.1);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	animation: modernOverlayFadeIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

@keyframes modernOverlayFadeIn {
	from {
		opacity: 0;
		transform: scale(0.9) translateY(10px);
	}
	to {
		opacity: 1;
		transform: scale(1) translateY(0);
	}
}

.modern-progress-container {
	width: 100%;
	max-width: 280px;
	padding: 1.5rem;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
}

/* Header Section */
.progress-header {
	display: flex;
	align-items: center;
	gap: 0.75rem;
}

.progress-icon-modern {
	flex-shrink: 0;
	width: 2.5rem;
	height: 2.5rem;
	background: linear-gradient(135deg, rgba(var(--vs-primary), 0.1) 0%, rgba(var(--vs-primary), 0.05) 100%);
	border-radius: 12px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.upload-cloud-icon {
	width: 1.5rem;
	height: 1.5rem;
	color: rgba(var(--vs-primary), 1);
	transition: all 0.3s ease;
}

.progress-icon-modern.success-icon {
	background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(34, 197, 94, 0.05) 100%);
}

.progress-icon-modern.success-icon .upload-cloud-icon {
	color: #22c55e;
}

.success-pulse {
	animation: successPulse 1s ease-in-out;
}

.redirecting-pulse {
	animation: redirectingMove 1.5s ease-in-out infinite;
	color: #059669 !important;
}

.redirecting-icon {
	background: linear-gradient(135deg, #059669, #10b981) !important;
	border: 2px solid #059669;
	animation: iconRedirectPulse 1s ease-in-out infinite;
}

@keyframes successPulse {
	0% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
	100% {
		transform: scale(1);
	}
}

.progress-status {
	flex: 1;
	min-width: 0;
}

.progress-main-title {
	font-size: 1rem;
	font-weight: 600;
	color: #1e293b;
	margin: 0 0 0.25rem 0;
	line-height: 1.2;
}

.progress-file-name {
	font-size: 0.8rem;
	color: #64748b;
	margin: 0;
	line-height: 1.2;
	word-break: break-all;
}

/* Progress Section */
.modern-progress-section {
	display: flex;
	flex-direction: column;
	gap: 0.75rem;
}

.progress-stats {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.progress-label {
	font-size: 0.8rem;
	font-weight: 500;
	color: #475569;
}

.progress-percent {
	font-size: 0.9rem;
	font-weight: 700;
	color: rgba(var(--vs-primary), 1);
}

/* Custom Progress Bar */
.custom-progress-bar {
	width: 100%;
}

.progress-track {
	width: 100%;
	height: 8px;
	background: #e2e8f0;
	border-radius: 8px;
	overflow: hidden;
	position: relative;
}

.progress-fill {
	height: 100%;
	background: linear-gradient(
		90deg,
		rgba(var(--vs-primary), 0.8) 0%,
		rgba(var(--vs-primary), 1) 50%,
		rgba(var(--vs-primary), 0.9) 100%
	);
	border-radius: 8px;
	position: relative;
	transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
	overflow: hidden;
}

.progress-fill.progress-complete {
	background: linear-gradient(90deg, #10b981 0%, #059669 50%, #10b981 100%);
}

.progress-shine {
	position: absolute;
	top: 0;
	left: -100%;
	width: 100%;
	height: 100%;
	background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.4) 50%, transparent 100%);
	animation: progressShine 2s infinite;
}

@keyframes progressShine {
	0% {
		left: -100%;
	}
	100% {
		left: 100%;
	}
}

/* Progress Details */
.progress-details {
	display: flex;
	align-items: center;
	justify-content: center;
}

.upload-speed,
.upload-complete {
	display: flex;
	align-items: center;
	font-size: 0.75rem;
	font-weight: 500;
	color: #64748b;
}

.upload-complete {
	color: #10b981 !important;
}

.upload-success-message {
	display: flex;
	align-items: center;
	font-size: 0.75rem;
	font-weight: 600;
	color: #10b981 !important;
	animation: fadeInSuccess 0.5s ease-in-out;
}

/* Redirecting Indicator Styles */
.redirecting-indicator {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 0.5rem;
	margin-top: 0.5rem;
	animation: fadeInRedirect 0.6s ease-in-out;
}

.redirecting-dots {
	display: flex;
	gap: 0.3rem;
	align-items: center;
}

.redirecting-dots .dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: #059669;
	animation: dotPulse 1.4s ease-in-out infinite;
}

.redirecting-dots .dot:nth-child(1) {
	animation-delay: 0s;
}

.redirecting-dots .dot:nth-child(2) {
	animation-delay: 0.2s;
}

.redirecting-dots .dot:nth-child(3) {
	animation-delay: 0.4s;
}

.redirecting-subtitle {
	font-size: 0.7rem;
	color: #059669;
	font-weight: 500;
	text-align: center;
	margin: 0;
	opacity: 0.8;
}

@keyframes fadeInSuccess {
	from {
		opacity: 0;
		transform: translateY(5px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInRedirect {
	from {
		opacity: 0;
		transform: translateY(3px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes pulseRedirect {
	0%,
	100% {
		transform: scale(1);
		opacity: 1;
	}
	50% {
		transform: scale(1.05);
		opacity: 0.8;
	}
}

@keyframes dotPulse {
	0%,
	20% {
		transform: scale(1);
		opacity: 0.3;
	}
	50% {
		transform: scale(1.2);
		opacity: 1;
	}
	80%,
	100% {
		transform: scale(1);
		opacity: 0.3;
	}
}

@keyframes redirectingMove {
	0%,
	100% {
		transform: translateX(0);
	}
	50% {
		transform: translateX(5px);
	}
}

@keyframes iconRedirectPulse {
	0%,
	100% {
		transform: scale(1);
		box-shadow: 0 0 0 0 rgba(5, 150, 105, 0.4);
	}
	50% {
		transform: scale(1.1);
		box-shadow: 0 0 0 10px rgba(5, 150, 105, 0);
	}
}

/* Actions */
.progress-actions-modern {
	display: flex;
	justify-content: center;
	margin-top: 0.5rem;
}

.cancel-upload-btn {
	border-radius: 6px !important;
	font-size: 0.8rem !important;
	padding: 0.4rem 1rem !important;
	transition: all 0.2s ease !important;
}

.cancel-upload-btn:hover {
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(239, 68, 68, 0.2);
}

/* File Ready Section Styles */
.file-ready-section {
	padding: 1rem;
	text-align: center;
	border-radius: 0.5rem;
	background: rgba(34, 197, 94, 0.05);
	border: 1px solid rgba(34, 197, 94, 0.2);
}

.file-status-info {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-bottom: 0.75rem;
}

.file-actions {
	display: flex;
	justify-content: center;
}

.redirecting-title {
	animation: pulseRedirect 1s ease-in-out infinite;
	color: #059669 !important;
	font-weight: 600 !important;
	font-size: 1rem !important;
}

/* Responsive Design */
@media (max-width: 768px) {
	.modern-progress-container {
		max-width: 240px;
		padding: 1rem;
		gap: 1rem;
	}

	.progress-icon-modern {
		width: 2rem;
		height: 2rem;
	}

	.upload-cloud-icon {
		width: 1.25rem;
		height: 1.25rem;
	}

	.progress-main-title {
		font-size: 0.9rem;
	}

	.progress-file-name {
		font-size: 0.7rem;
	}

	.progress-track {
		height: 6px;
	}

	.progress-label,
	.progress-percent {
		font-size: 0.75rem;
	}
}

@media (max-width: 480px) {
	.modern-progress-container {
		max-width: 200px;
		padding: 0.75rem;
	}

	.progress-header {
		gap: 0.5rem;
	}

	.progress-main-title {
		font-size: 0.85rem;
	}

	.progress-file-name {
		font-size: 0.65rem;
	}
}
</style>
